import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShoppingBag, CalendarClock, Truck, Store, Euro, Plus, QrCode, Link2, Download, Trash2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import DashboardShell, { StatCard, EmptyState } from "@/components/DashboardShell";
import OrderCard from "@/components/OrderCard";
import { eur, STATUS_LABEL } from "@/lib/status";
import api, { API, apiError } from "@/lib/api";
import ProfileEditor from "@/components/ProfileEditor";
import { downloadPdf } from "@/lib/download";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function BnbDashboard() {
  const [tab, setTab] = useState("overview");
  const [me, setMe] = useState(null);
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [available, setAvailable] = useState([]);
  const [packages, setPackages] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [newOrderOpen, setNewOrderOpen] = useState(false);

  const load = () => {
    api.get("/bnb/me").then(({ data }) => setMe(data)).catch(() => {});
    api.get("/bnb/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/bnb/orders").then(({ data }) => setOrders(data)).catch(() => {});
    api.get("/bnb/partnerships").then(({ data }) => setVendors(data)).catch(() => {});
    api.get("/bnb/vendors/available").then(({ data }) => setAvailable(data)).catch(() => {});
    api.get("/bnb/packages").then(({ data }) => setPackages(data)).catch(() => {});
    api.get("/bnb/rooms").then(({ data }) => setRooms(data)).catch(() => {});
  };
  useEffect(load, []);

  const requestPartnership = async (vendorId) => {
    try { await api.post("/bnb/partnership-request", { vendor_id: vendorId }); toast.success("Richiesta inviata"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const addRoom = async (number, greeting) => {
    try { await api.post("/bnb/rooms", { number, greeting }); toast.success("Camera aggiunta"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const delRoom = async (id) => { await api.delete(`/bnb/rooms/${id}`); load(); };

  const tabs = [
    { key: "overview", label: "Panoramica" },
    { key: "orders", label: "Ordini" },
    { key: "packages", label: "Pacchetti" },
    { key: "vendors", label: "Locali" },
    { key: "rooms", label: "Camere & QR" },
    { key: "profile", label: "Profilo" },
  ];

  return (
    <DashboardShell title={me?.name || "Dashboard B&B"} subtitle="Gestisci ordini, pacchetti e QR code" tabs={tabs} active={tab} onTab={setTab}
      headerRight={<Button onClick={() => setNewOrderOpen(true)} className="rounded-full bg-orange hover:bg-orange-600 text-white" data-testid="new-order-btn"><Plus className="w-4 h-4 mr-1" />Nuovo ordine</Button>}>
      {tab === "overview" && stats && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard label="Ordini oggi" value={stats.today} icon={ShoppingBag} />
            <StatCard label="Ordini futuri" value={stats.future} icon={CalendarClock} />
            <StatCard label="In consegna" value={stats.delivering} icon={Truck} />
            <StatCard label="Locali collegati" value={stats.linked_vendors} icon={Store} />
            <StatCard label="Margine del mese" value={eur(stats.month_margin)} icon={Euro} />
            <StatCard label="Ordini totali" value={stats.total_orders} icon={ShoppingBag} />
          </div>
          <div className="flex flex-wrap gap-3">
            <Button onClick={() => setNewOrderOpen(true)} variant="outline" className="rounded-full"><Plus className="w-4 h-4 mr-1" />Nuovo ordine</Button>
            <Button onClick={() => setTab("packages")} variant="outline" className="rounded-full"><Store className="w-4 h-4 mr-1" />Gestisci pacchetti</Button>
            <Button onClick={() => setTab("rooms")} variant="outline" className="rounded-full"><QrCode className="w-4 h-4 mr-1" />Genera QR code</Button>
            <Button onClick={() => setTab("vendors")} variant="outline" className="rounded-full"><Link2 className="w-4 h-4 mr-1" />Invita un bar</Button>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button onClick={() => downloadPdf(`/bnb/settlement.pdf?period=month`, "riepilogo-mese.pdf")} variant="outline" className="rounded-full" data-testid="bnb-settlement-month"><Download className="w-4 h-4 mr-1" />Riepilogo mese (PDF)</Button>
            <Button onClick={() => downloadPdf(`/bnb/settlement.pdf?period=week`, "riepilogo-settimana.pdf")} variant="outline" className="rounded-full" data-testid="bnb-settlement-week"><Download className="w-4 h-4 mr-1" />Riepilogo settimana (PDF)</Button>
          </div>
        </div>
      )}

      {tab === "orders" && (
        <div className="grid md:grid-cols-2 gap-4">
          {orders.length === 0 && <EmptyState text="Nessun ordine. Crea il primo ordine per un ospite." />}
          {orders.map((o) => <OrderCard key={o.id} order={o} showEconomics showWhatsapp />)}
        </div>
      )}

      {tab === "packages" && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {packages.length === 0 && <EmptyState text="Nessun pacchetto disponibile. Collegati a un locale per vedere i pacchetti." />}
          {packages.map((p) => {
            const guest = (p.vendor_price_pp + (me?.default_margin_pp || 0.6) + 0.5).toFixed(2).replace(".", ",");
            return (
              <div key={p.id} className="bg-white rounded-2xl border border-navy/10 p-5">
                <div className="flex justify-between items-start">
                  <p className="font-semibold text-navy">{p.name}</p>
                  <Badge variant="outline" className="text-xs">{p.vendor_name}</Badge>
                </div>
                <p className="text-xs text-navy/50 mt-1">{p.description}</p>
                <div className="mt-3 text-sm space-y-1">
                  <div className="flex justify-between"><span className="text-navy/50">Costo locale</span><span className="text-navy">{eur(p.vendor_price_pp)}</span></div>
                  <div className="flex justify-between"><span className="text-navy/50">Prezzo ospite (stima)</span><span className="font-bold text-orange">{guest} €</span></div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {tab === "vendors" && (
        <div className="space-y-3">
          <p className="text-sm text-navy/50">Locali approvati nella tua città. Invia una richiesta di convenzione.</p>
          {available.length === 0 && <EmptyState text="Nessun locale disponibile nella tua città al momento." />}
          {available.map((v) => (
            <div key={v.id} className="bg-white rounded-2xl border border-navy/10 p-4 flex items-center justify-between gap-3" data-testid={`avail-vendor-${v.id}`}>
              <div>
                <p className="font-medium text-navy">{v.name} <span className="text-xs text-navy/40 capitalize">· {v.type}</span></p>
                <p className="text-xs text-navy/50">{v.address}, {v.city} · {v.hours}</p>
              </div>
              {v.linked ? <Badge className="bg-successtext text-white">Collegato</Badge>
                : v.requested ? <Badge variant="outline">Richiesta inviata</Badge>
                : <Button size="sm" onClick={() => requestPartnership(v.id)} className="rounded-full bg-navy text-white" data-testid={`request-${v.id}`}><Link2 className="w-4 h-4 mr-1" />Richiedi</Button>}
            </div>
          ))}
        </div>
      )}

      {tab === "rooms" && <RoomsTab rooms={rooms} bnbId={me?.id} onAdd={addRoom} onDel={delRoom} />}

      {tab === "profile" && <ProfileEditor role="bnb" me={me} onSaved={load} />}

      <NewOrderDialog open={newOrderOpen} onOpenChange={setNewOrderOpen} vendors={vendors} me={me} onCreated={() => { setNewOrderOpen(false); load(); }} />
    </DashboardShell>
  );
}

function RoomsTab({ rooms, bnbId, onAdd, onDel }) {
  const [num, setNum] = useState("");
  const [greet, setGreet] = useState("");
  const origin = encodeURIComponent(window.location.origin);
  const qrUrl = (room, ext) => `${API}/qr/bnb/${bnbId}.${ext}?origin=${origin}${room ? `&room=${room}` : ""}`;
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl border border-navy/10 p-5">
        <p className="font-semibold text-navy mb-3">QR code generale struttura</p>
        <div className="flex flex-wrap items-center gap-3">
          <img src={qrUrl("", "png")} alt="QR struttura" className="w-32 h-32 rounded-xl border border-navy/10" />
          <div className="flex flex-col gap-2">
            <a href={qrUrl("", "png")} download><Button variant="outline" className="rounded-full" data-testid="qr-general-png"><Download className="w-4 h-4 mr-1" />PNG</Button></a>
            <a href={qrUrl("", "pdf")}><Button variant="outline" className="rounded-full" data-testid="qr-general-pdf"><Download className="w-4 h-4 mr-1" />PDF stampabile</Button></a>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-navy/10 p-5">
        <p className="font-semibold text-navy mb-3">Aggiungi camera</p>
        <div className="flex flex-col sm:flex-row gap-2">
          <Input placeholder="Numero camera" value={num} onChange={(e) => setNum(e.target.value)} className="rounded-xl" data-testid="room-number" />
          <Input placeholder="Saluto personalizzato (facoltativo)" value={greet} onChange={(e) => setGreet(e.target.value)} className="rounded-xl flex-1" data-testid="room-greeting" />
          <Button onClick={() => { if (num) { onAdd(num, greet); setNum(""); setGreet(""); } }} className="rounded-full bg-navy text-white" data-testid="add-room-btn"><Plus className="w-4 h-4 mr-1" />Aggiungi</Button>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {rooms.map((r) => (
          <div key={r.id} className="bg-white rounded-2xl border border-navy/10 p-4" data-testid={`room-${r.number}`}>
            <div className="flex items-center justify-between">
              <p className="font-semibold text-navy">Camera {r.number}</p>
              <button onClick={() => onDel(r.id)} className="text-errortext"><Trash2 className="w-4 h-4" /></button>
            </div>
            <img src={qrUrl(r.number, "png")} alt="" className="w-full aspect-square object-contain rounded-xl border border-navy/10 mt-3 bg-white" />
            <div className="flex gap-2 mt-3">
              <a href={qrUrl(r.number, "png")} download className="flex-1"><Button variant="outline" size="sm" className="w-full rounded-full">PNG</Button></a>
              <a href={qrUrl(r.number, "pdf")} className="flex-1"><Button variant="outline" size="sm" className="w-full rounded-full">PDF</Button></a>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function NewOrderDialog({ open, onOpenChange, vendors, me, onCreated }) {
  const [vendorId, setVendorId] = useState("");
  const [pkgs, setPkgs] = useState([]);
  const [f, setF] = useState({ package_id: "", room: "", guest_name: "", guest_phone: "", persons: 1, date: "", time_slot: "08:00 - 09:00", mode: "pickup", notes: "", payment_method: "property", margin_pp: me?.default_margin_pp || 0.6 });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setF({ ...f, [k]: v });

  useEffect(() => {
    if (vendorId) api.get("/bnb/packages").then(({ data }) => setPkgs(data.filter((p) => p.vendor_id === vendorId))).catch(() => {});
  }, [vendorId]);

  const submit = async () => {
    if (!vendorId || !f.package_id || !f.guest_name || !f.date) { toast.error("Compila tutti i campi obbligatori"); return; }
    setLoading(true);
    try {
      await api.post("/bnb/orders", { ...f, vendor_id: vendorId, persons: Number(f.persons), margin_pp: Number(f.margin_pp) });
      toast.success("Ordine creato");
      onCreated();
      setVendorId(""); setF({ ...f, package_id: "", guest_name: "", room: "", date: "" });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl max-h-[90vh] overflow-auto" data-testid="new-order-dialog">
        <DialogHeader><DialogTitle className="text-navy font-heading">Nuovo ordine</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Locale convenzionato</Label>
            <Select value={vendorId} onValueChange={setVendorId}>
              <SelectTrigger className="rounded-xl mt-1" data-testid="order-vendor"><SelectValue placeholder="Seleziona locale" /></SelectTrigger>
              <SelectContent>{vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {vendors.length === 0 && <p className="text-xs text-errortext">Nessun locale collegato. Vai in "Locali" per richiedere una convenzione.</p>}
          <div>
            <Label>Pacchetto</Label>
            <Select value={f.package_id} onValueChange={(v) => set("package_id", v)}>
              <SelectTrigger className="rounded-xl mt-1" data-testid="order-package"><SelectValue placeholder="Seleziona pacchetto" /></SelectTrigger>
              <SelectContent>{pkgs.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {eur(p.vendor_price_pp)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Camera</Label><Input value={f.room} onChange={(e) => set("room", e.target.value)} className="rounded-xl mt-1" data-testid="order-room" /></div>
            <div><Label>Persone</Label><Input type="number" min="1" value={f.persons} onChange={(e) => set("persons", e.target.value)} className="rounded-xl mt-1" data-testid="order-persons" /></div>
            <div><Label>Nome ospite</Label><Input value={f.guest_name} onChange={(e) => set("guest_name", e.target.value)} className="rounded-xl mt-1" data-testid="order-guest" /></div>
            <div><Label>Telefono</Label><Input value={f.guest_phone} onChange={(e) => set("guest_phone", e.target.value)} className="rounded-xl mt-1" data-testid="order-guestphone" /></div>
            <div><Label>Giorno</Label><Input type="date" value={f.date} onChange={(e) => set("date", e.target.value)} className="rounded-xl mt-1" data-testid="order-date" /></div>
            <div><Label>Fascia oraria</Label>
              <select value={f.time_slot} onChange={(e) => set("time_slot", e.target.value)} className="w-full rounded-xl mt-1 h-10 border border-input px-3 text-sm" data-testid="order-slot">
                {["07:00 - 08:00", "08:00 - 09:00", "09:00 - 10:00", "10:00 - 11:00"].map((s) => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div><Label>Margine € /pers.</Label><Input type="number" step="0.01" value={f.margin_pp} onChange={(e) => set("margin_pp", e.target.value)} className="rounded-xl mt-1" data-testid="order-margin" /></div>
          </div>
          <div>
            <Label>Modalità</Label>
            <RadioGroup value={f.mode} onValueChange={(v) => set("mode", v)} className="flex gap-4 mt-2">
              <label className="flex items-center gap-2 text-sm"><RadioGroupItem value="pickup" data-testid="order-pickup" /> Ritiro</label>
              <label className="flex items-center gap-2 text-sm"><RadioGroupItem value="delivery" data-testid="order-delivery" /> Consegna</label>
            </RadioGroup>
          </div>
          <div><Label>Note</Label><Textarea value={f.notes} onChange={(e) => set("notes", e.target.value)} className="rounded-xl mt-1" rows={2} data-testid="order-notes" /></div>
          <Button onClick={submit} disabled={loading} className="w-full rounded-full bg-navy text-white h-11" data-testid="order-submit">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Crea ordine"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
